import { Avatar, Button, Stack, SxProps, Typography } from '@mui/material';
import { useAppSelector, useDictionary } from 'Hooks';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { MultiToggleInput } from 'Components/HookFormInputs/MultiToggleInput';
import { RadioInput } from 'Components/HookFormInputs/RadioInput';
import { AutoCompleteInput } from 'Components/HookFormInputs/AutoCompleteInput';
import { useFormContext } from 'react-hook-form';
import { ITherapySuppliers } from 'Interfaces/therapySuppliers';
import { useContext, useMemo, useRef, useState } from 'react';
import { SuppliersContext, defaultFilters } from 'Context/SuppliersContext';
import { SUPPLIERS_RESOURCES, SUPPLIERS_STEPS } from 'Enum/suppliers';
import { IGeneralOptions } from 'API/generalOptions/generalOptions';
import { LeftArrowWhite } from 'Images';
import { TagsInput } from 'Components/HookFormInputs/TagsInput';

interface IForm {
  formSx?: SxProps;
}
export const Form = ({ formSx }: IForm) => {
  const [locationStepId, setLocationStepId] = useState('1');
  const { register, handleSubmit, resetField, setValue, watch } =
    useFormContext<ITherapySuppliers>();
  const {
    resources,
    supplierNames,
    setFilters,
    setStepIndex,
    setIsAllFiltersModal,
  } = useContext(SuppliersContext);

  const generalOptions: IGeneralOptions | null = useAppSelector(
    (generalOptions) => generalOptions.generalOptions.generalOptions
  );

  const dictionary = useDictionary(
    Dictionary,
    DICTIONARY.Suppliers
  ).therapySearchForm;

  const locationSteps = useMemo(() => {
    return [
      { value: '1', label: dictionary.area, name: 'area' },
      { value: '2', label: dictionary.settlement, name: 'city' },
      { value: '3', label: dictionary.county, name: 'region' },
    ];
  }, [dictionary]);

  const getLocationOptions = () => {
    switch (locationStepId) {
      case locationSteps[0].value:
        return (
          resources[SUPPLIERS_RESOURCES.region]?.resources.map((resource) => ({
            id: resource.resourceId,
            value: resource.content,
          })) || []
        );

      case locationSteps[1].value:
        return generalOptions?.cities || [];

      case locationSteps[2].value:
        return (
          resources[SUPPLIERS_RESOURCES.region]?.resources.map((resource) => ({
            id: resource.resourceId,
            value: resource.content,
          })) || []
        );
    }
  };

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    await handleSubmit(
      (data) => {
        setFilters({
          ...defaultFilters,
          accessible: data.accessible,
          area: data.area,
          city: data.city,
          region: data.region,
          specialty: data.specialty,
          supName: data.supName,
          gender: data.gender,
          treatMethod: data.treatMethod,
        });
        setIsAllFiltersModal(false);
        // setStepIndex(SUPPLIERS_STEPS.RESULTS);
        console.log({
          ...defaultFilters,
          accessible: data.accessible,
          area: data.area,
          city: data.city,
          region: data.region,
          specialty: data.specialty,
          supName: data.supName,
          gender: data.gender,
          treatMethod: data.treatMethod,
          data,
        });
        setStepIndex(SUPPLIERS_STEPS.RESULTS);
      },
      (error) => console.log({ error })
    )();
  };

  const isLocationReset = useRef<boolean>(false);

  return (
    <Stack
      sx={{
        maxWidth: '770px',
        padding: '16px 24px 48px',
        alignItems: 'center',
        borderRadius: '16px',
        border: '1px solid',
        borderColor: 'primary.shade_100',
        backgroundColor: 'white',
        boxShadow: '-2px -2px 12px 0px rgba(8, 30, 84, 0.04)',
        ...formSx,
      }}
      component='form'
      onSubmit={onSubmit}
    >
      <Button sx={{ alignSelf: 'flex-end', p: '2px 0 0' }}>
        <Typography>{dictionary.reset}</Typography>
      </Button>
      <Stack gap='40px' maxWidth='400px'>
        <Stack gap='20px'>
          <Typography variant='body2'>{dictionary.specialization}</Typography>
          <AutoCompleteInput
            options={
              resources[SUPPLIERS_RESOURCES.profession]?.resources.map(
                (resource) => ({
                  id: resource.resourceId,
                  value: resource.content,
                })
              ) || []
            }
            name={'specialty'}
          />
        </Stack>
        {/* {subSpecialties.resources && (
          <Stack gap='40px' maxWidth='400px'>
            <Stack gap='20px'>
              <Typography variant='body2'>
                {dictionary.subSpecialization}
              </Typography>
              <AutoCompleteInput
                options={
                  subSpecialties.resources.map((resource) => ({
                    id: resource.resourceId,
                    value: resource.content,
                  })) || []
                }
                name={'subSpecialty'}
              />
            </Stack>
          </Stack>
        )} */}
        <Stack gap='20px'>
          <Typography variant='body2'>{dictionary.location}</Typography>
          <MultiToggleInput
            toggleButtons={locationSteps}
            value={locationStepId}
            onChange={(_, value) => {
              setLocationStepId(value || '1');
              resetField('area', { defaultValue: undefined });
              resetField('city', { defaultValue: undefined });
              resetField('region', { defaultValue: undefined });
              isLocationReset.current = true;
            }}
          />

          <Stack gap='20px'>
            <AutoCompleteInput
              options={getLocationOptions() || []}
              name={
                locationSteps.find((step) => step.value === locationStepId)
                  ?.name || ''
              }
              isReset={isLocationReset}
            />
          </Stack>
        </Stack>
        <Stack
          gap='20px'
          sx={{
            '.MuiSvgIcon-root > path': {
              strokeWidth: '0.833333',
              stroke: '#A020FF',
            },
          }}
        >
          <Typography variant='body2'>{dictionary.isAccessible}</Typography>
          <RadioInput
            options={[
              { value: true, label: dictionary.yes },
              { value: false, label: dictionary.no },
            ]}
            name={'accessible'}
          />
        </Stack>
        {supplierNames.length > 0 && (
          <Stack gap='20px'>
            <Typography variant='body2'>{dictionary.therapistsName}</Typography>
            <AutoCompleteInput
              options={
                supplierNames.map((name) => ({
                  id: name,
                  value: name,
                })) || []
              }
              name={'supName'}
            />
          </Stack>
        )}
        {/* <Stack gap='20px'>
          <Typography variant='body2'>{dictionary.languages}</Typography>
          <AutoCompleteInput
            options={
              resources[SUPPLIERS_RESOURCES.language]?.resources.map(
                (resource) => ({
                  id: resource.resourceId,
                  value: resource.content,
                })
              ) || []
            }
            name={'lng'}
          />
        </Stack> */}
        <Stack
          gap='20px'
          sx={{
            '.MuiSvgIcon-root > path': {
              strokeWidth: '0.833333',
              stroke: '#A020FF',
            },
          }}
        >
          <Typography variant='body2'>{dictionary.caregiverGender}</Typography>
          <RadioInput
            options={[
              { value: -1, label: dictionary.no },
              {
                value: generalOptions?.general?.genders[0].id || 1,
                label: dictionary.male,
              },
              {
                value: generalOptions?.general?.genders[1].id || 2,
                label: dictionary.female,
              },
            ]}
            name={'gender'}
          />
        </Stack>
        <Stack
          gap='20px'
          sx={{
            '.MuiSvgIcon-root > path': {
              strokeWidth: '0.833333',
              stroke: '#A020FF',
            },
          }}
        >
          <Typography variant='body2'>{dictionary.treatmentMethods}</Typography>
          <TagsInput
            filterTags={[
              { label: 'aaa', value: 1 },
              { label: 'bbb', value: 2 },
              { label: 'ccc', value: 3 },
            ]}
            isActive={(tag) =>
              !!watch('treatMethod')?.find((t) => t === tag.value)
            }
            resetTags={() => {
              setValue('treatMethod', []);
            }}
            toggleFilter={(tag) => {
              let newTreatMethod = watch('treatMethod') || [];
              const tagIndex = watch('treatMethod')?.find(
                (t) => t === tag.value
              );
              if (!!tagIndex) {
                newTreatMethod?.splice(tagIndex, 1);
              } else {
                newTreatMethod?.push(tag.value);
              }
              setValue('treatMethod', newTreatMethod);
            }}
          />
        </Stack>
      </Stack>
      <Button
        variant='contained'
        sx={{ height: '46px', width: '240px', gap: '4px', mt: '64px' }}
        type='submit'
      >
        <Typography variant='body4'>{'חיפוש'}</Typography>
        <Avatar
          alt=''
          src={LeftArrowWhite}
          sx={{ height: '20px', width: '20px' }}
        />
      </Button>
    </Stack>
  );
};
